/* eslint-disable @typescript-eslint/no-explicit-any */
import dayjs from 'dayjs';
import { jwtDecode } from 'jwt-decode';

import type { User } from '@/models/users';

interface JwtPayload {
   exp: number;
}

const DELAY_REFRESH_TOKEN = 110;

export const checkJWT = (jwt: string) => {
   const expirationDate = dayjs.unix(jwtDecode<JwtPayload>(jwt).exp);
   const diffInSecond = expirationDate.diff(dayjs(), 'second');

   if (diffInSecond > DELAY_REFRESH_TOKEN) return 'valid';
   else {
      return 'refreshable';
   }
};

export const downloadMedia = (
   data: ArrayBuffer,
   name: string,
   type?: string,
) => {
   const newBlob = new Blob([data], { type: type });
   const url = window.URL.createObjectURL(newBlob);
   const link = document.createElement('a');

   link.setAttribute('download', `${name}`);
   link.setAttribute('href', url);
   document.body.appendChild(link);
   link.click();
   window.URL.revokeObjectURL(url);
};

export const emailRegex =
   /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const getObjectKey = (obj: any, path: string): any => {
   path = path.replaceAll('[', '.');
   path = path.replaceAll(']', '');

   const keys = path.split('.').filter(Boolean);
   let current = obj;

   for (const key of keys) {
      current = current[key];
      if (current === undefined) {
         return undefined;
      }
   }
   return current;
};

export const checkEndDateBeforeOpenSurvey = (
   expireDate: string | Date | null,
   oldStatus: string,
   newStatus: string,
): boolean => {
   if (!expireDate) return false;
   const now = Date.now();
   const statusToCheck = ['draft', 'paused'];
   if (!statusToCheck.includes(oldStatus) || newStatus !== 'open') return false;

   const differenceBetweenExpireAndNowDate = dayjs(expireDate).diff(now) > 0;
   if (differenceBetweenExpireAndNowDate) return false;

   return true;
};

export const parseJwt = (token: string) => {
   return JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
};

export const getFullname = (
   user: Pick<User, 'firstname' | 'lastname'> | null | undefined,
   defaultValue?: string,
): string => {
   return user
      ? `${user.lastname || ''} ${user.firstname || ''}`.trim()
      : (defaultValue ?? '');
};

export const getStatusStyle = (statusLabel: string) => {
   const styles = {
      draft: {
         color: '#909090',
         backgroundColor: 'rgba(144, 144, 144, 0.2)',
         borderColor: 'rgba(144, 144, 144, 0.3)',
      },
      open: {
         color: '#FFBE0B',
         backgroundColor: 'rgba(255, 190, 11, 0.2)',
         borderColor: 'rgba(255, 190, 11, 0.3)',
      },
      closed: {
         color: '#408538',
         backgroundColor: 'rgba(64, 133, 56, 0.2)',
         borderColor: 'rgba(64, 133, 56, 0.3)',
      },
      paused: {
         color: '#CF2A2A',
         backgroundColor: 'rgba(207, 42, 42, 0.2)',
         borderColor: 'rgba(207, 42, 42, 0.3)',
      },
   };
   return styles[statusLabel as keyof typeof styles] || styles.draft;
};
